import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2, Ticket, RefreshCw, Clock, Download, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { GAM_TICKET_LOGO } from '@/config/assets';

// Verification config - faster polling for immediate feedback
const POLL_INTERVAL = 1500; // Check every 1.5 seconds for faster feedback
const MAX_POLL_ATTEMPTS = 120; // Max 3 minutes of polling

export default function PaymentResultPage() {
  // Support both URL params and query params
  const { paymentId: paramPaymentId, status: paramStatus } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  
  // Prefer query params for ModemPay redirects, fall back to URL params
  const urlStatus = query.get('status') || paramStatus;
  const paymentId = query.get('payment_id') || paramPaymentId;
  
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [payment, setPayment] = useState(null);
  const [ticketDetails, setTicketDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const pollIntervalRef = useRef(null);

  // Fetch full ticket details including QR data
  const fetchTicketDetails = async (ticketIds) => {
    if (!ticketIds || ticketIds.length === 0) return;
    
    try {
      const details = await Promise.all(
        ticketIds.map(async (ticketId) => {
          try {
            const response = await axios.get(`${API_URL}/api/tickets/${ticketId}`);
            return response.data;
          } catch (err) {
            console.error(`Failed to fetch ticket ${ticketId}:`, err);
            return { ticket_id: ticketId, error: true };
          }
        })
      );
      setTicketDetails(details.filter(t => !t.error));
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
    }
  };

  // Download PDF for a specific ticket
  const downloadPDF = async (ticketId) => {
    try {
      setDownloading(ticketId);
      const response = await axios.get(
        `${API_URL}/api/tickets/${ticketId}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloading(null);
    }
  };

  // Memoized check function
  const checkPaymentStatus = useCallback(async () => {
    if (!paymentId) {
      setLoading(false);
      return;
    }
    
    setChecking(true);
    try {
      // Use the verify endpoint that checks with ModemPay
      const response = await axios.get(
        `${API_URL}/api/pay/verify/${paymentId}`
      );
      
      console.log('Payment verification response:', response.data);
      setPayment(response.data);
      
      // If confirmed, fetch ticket details immediately for QR display
      if (response.data.status === 'confirmed' && response.data.tickets?.length > 0) {
        await fetchTicketDetails(response.data.tickets);
        
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
      
      // If rejected/cancelled, stop polling
      if (response.data.status === 'rejected' || response.data.status === 'cancelled') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to check payment status:', error);
      // Try old endpoint as fallback
      try {
        const fallbackResponse = await axios.get(
          `${API_URL}/api/payments/status/${paymentId}`,
          getAuthHeader()
        );
        setPayment(fallbackResponse.data);
        
        // Also fetch tickets on fallback success
        if (fallbackResponse.data.status === 'confirmed' && fallbackResponse.data.tickets?.length > 0) {
          await fetchTicketDetails(fallbackResponse.data.tickets);
        }
        
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error('Fallback check also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, [paymentId, getAuthHeader]);

  // Initial check and polling setup
  useEffect(() => {
    // Initial check
    checkPaymentStatus();
    
    // Set up polling for pending payments
    if (paymentId && (urlStatus === 'pending' || urlStatus === 'success')) {
      pollIntervalRef.current = setInterval(() => {
        setPollCount(prev => {
          if (prev >= MAX_POLL_ATTEMPTS) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            return prev;
          }
          checkPaymentStatus();
          return prev + 1;
        });
      }, POLL_INTERVAL);
    }
    
    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [paymentId, urlStatus, checkPaymentStatus]);

  // Manual refresh
  const handleManualCheck = async () => {
    setPollCount(0);
    await checkPaymentStatus();
    
    // Restart polling if still pending
    if (payment?.status === 'pending' && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => {
        setPollCount(prev => {
          if (prev >= MAX_POLL_ATTEMPTS) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            return prev;
          }
          checkPaymentStatus();
          return prev + 1;
        });
      }, POLL_INTERVAL);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#0055FF] mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  const isSuccess = payment?.status === 'confirmed';
  const isCancelled = urlStatus === 'cancel' || payment?.status === 'cancelled';
  const isFailed = payment?.status === 'rejected';
  const isPending = payment?.status === 'pending';

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="h-12 w-auto" />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            {isSuccess ? (
              <>
                <div className="w-16 h-16 bg-[#00C853] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">Payment Successful!</h1>
                <p className="text-muted-foreground text-sm mb-4">
                  Your tickets are ready. Show the QR code at the venue entrance.
                </p>
                
                {/* Show QR codes immediately */}
                {ticketDetails.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    {ticketDetails.map((ticket, index) => (
                      <div key={ticket.ticket_id} className="bg-gradient-to-b from-gray-50 to-white border border-gray-200 rounded-xl p-4 shadow-sm">
                        {/* Event Info */}
                        <div className="text-center mb-3">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            {ticketDetails.length > 1 ? `Ticket ${index + 1} of ${ticketDetails.length}` : 'Your Ticket'}
                          </p>
                          <h3 className="font-bold text-gray-900 text-sm mt-1">
                            {ticket.event_name || ticket.fixture_name || 'Event Ticket'}
                          </h3>
                          {ticket.fixture_date && (
                            <p className="text-xs text-gray-600 mt-1">
                              {new Date(ticket.fixture_date).toLocaleDateString('en-GB', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                        
                        {/* QR Code - Prominent Display */}
                        <div className="bg-white p-4 rounded-lg border border-gray-100 mb-3">
                          <QRCode
                            value={ticket.ticket_id}
                            size={200}
                            level="L"
                            style={{ 
                              width: '100%', 
                              maxWidth: '200px', 
                              height: 'auto',
                              margin: '0 auto',
                              display: 'block'
                            }}
                          />
                        </div>
                        
                        {/* Ticket ID */}
                        <div className="text-center mb-3">
                          <span className="font-mono text-sm font-bold text-[#0055FF] bg-blue-50 px-3 py-1 rounded-full">
                            {ticket.ticket_id}
                          </span>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 text-xs"
                            onClick={() => downloadPDF(ticket.ticket_id)}
                            disabled={downloading === ticket.ticket_id}
                          >
                            {downloading === ticket.ticket_id ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3 mr-1" />
                            )}
                            Download PDF
                          </Button>
                          <Link to={`/ticket/${ticket.ticket_id}`} className="flex-1">
                            <Button size="sm" variant="outline" className="w-full text-xs">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Full View
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : payment?.tickets && payment.tickets.length > 0 ? (
                  <div className="space-y-2 mb-6">
                    <p className="text-sm font-medium">Your Ticket IDs:</p>
                    {payment.tickets.map((ticketId) => (
                      <div key={ticketId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-mono font-semibold">{ticketId}</span>
                        <Link to={`/ticket/${ticketId}`}>
                          <Button size="sm" variant="outline">View QR</Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="flex gap-3">
                  <Link to="/my-tickets" className="flex-1">
                    <Button className="w-full bg-[#0055FF] hover:bg-[#0044CC]">
                      <Ticket className="w-4 h-4 mr-2" />
                      All My Tickets
                    </Button>
                  </Link>
                  <Link to="/" className="flex-1">
                    <Button variant="outline" className="w-full">
                      Browse Events
                    </Button>
                  </Link>
                </div>
              </>
            ) : isCancelled ? (
              <>
                <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-white" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Payment Cancelled</h1>
                <p className="text-muted-foreground mb-6">
                  Your payment was cancelled. No charges were made.
                </p>
                <Link to="/">
                  <Button className="bg-[#0055FF] hover:bg-[#0044CC]">
                    Back to Events
                  </Button>
                </Link>
              </>
            ) : isFailed ? (
              <>
                <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-white" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Payment Failed</h1>
                <p className="text-muted-foreground mb-6">
                  {payment?.message || "Your payment could not be processed. Please try again."}
                </p>
                <Link to="/">
                  <Button className="bg-[#0055FF] hover:bg-[#0044CC]">
                    Back to Events
                  </Button>
                </Link>
              </>
            ) : isPending ? (
              <>
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-white animate-pulse" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">Confirming Payment...</h1>
                <p className="text-muted-foreground text-sm mb-4">
                  We're confirming your payment. Your QR code will appear in seconds.
                </p>
                
                {/* Progress indicator */}
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-2 text-sm text-blue-600 mb-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Verifying with ModemPay...</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((pollCount / MAX_POLL_ATTEMPTS) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Button 
                    onClick={handleManualCheck} 
                    disabled={checking} 
                    className="w-full bg-[#0055FF] hover:bg-[#0044CC]"
                  >
                    {checking ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Refresh Status
                  </Button>
                  
                  <p className="text-xs text-muted-foreground">
                    Payment confirmed? If your QR doesn't appear within 30 seconds, tap Refresh.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-2">Processing...</h1>
                <p className="text-muted-foreground mb-6">
                  Please wait while we process your payment.
                </p>
                <Button onClick={handleManualCheck} disabled={checking} variant="outline">
                  {checking ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Check Status
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
